import { Link } from "react-router-dom";
import { useEffect, useState, useMemo } from "react";
import { ArrowRight, Sparkles, Users, Award, Target, Zap, Brain, Calendar, ChevronRight, Star, Quote } from "lucide-react";
import api from "@/lib/api";
import { useSettings } from "@/lib/settings";
import Counter from "@/components/Counter";
import LeadForm from "@/components/LeadForm";
import HeroSlider from "@/components/HeroSlider";
import MobileAppSection from "@/components/MobileAppSection";

export default function HomePage() {
  const { settings } = useSettings();
  const [courses, setCourses] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);
  const [blogs, setBlogs] = useState([]);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/courses").then(({ data }) => setCourses(data)).catch(() => {});
    api.get("/testimonials").then(({ data }) => setTestimonials(data)).catch(() => {});
    api.get("/faqs").then(({ data }) => setFaqs(data)).catch(() => {});
    api.get("/blogs").then(({ data }) => setBlogs(data)).catch(() => {});
    api.get("/events").then(({ data }) => setEvents(data)).catch(() => {});
  }, []);

  const sectionVis = useMemo(() => {
    const map = {};
    (settings?.homepage_sections || []).forEach((s) => { map[s.key] = s.visible; });
    return map;
  }, [settings]);

  const show = (k) => sectionVis[k] !== false; // default show

  // FAQPage structured data so AI assistants answer common questions accurately
  useEffect(() => {
    const id = "ld-faq-home";
    let el = document.getElementById(id);
    if (!faqs.length) { if (el) el.remove(); return; }
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = id;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.question,
        acceptedAnswer: { "@type": "Answer", text: f.answer },
      })),
    });
    return () => { const e = document.getElementById(id); if (e) e.remove(); };
  }, [faqs]);

  const displayedCourses = useMemo(() => {
    if (!courses.length) return [];
    const featSlugs = settings?.home_featured_course_slugs || [];
    if (featSlugs.length > 0) {
      const pinned = featSlugs.map((sl) => courses.find((c) => c.slug === sl)).filter(Boolean);
      const rest = courses.filter((c) => !featSlugs.includes(c.slug));
      return [...pinned, ...rest].slice(0, 3);
    }
    const featured = courses.filter((c) => c.featured);
    return (featured.length >= 3 ? featured : [...featured, ...courses.filter((c) => !c.featured)]).slice(0, 3);
  }, [courses, settings]);

  if (!settings) return <div className="min-h-screen flex items-center justify-center text-neutral-500">Loading...</div>;

  const s = settings;

  return (
    <>
      {show("hero") && <HeroSlider />}

      {show("stats") && (
        <section className="bg-neutral-950 text-white py-20" data-testid="stats-section">
          <div className="container-eq grid grid-cols-2 md:grid-cols-4 gap-8">
            {s.stats.map((stat, i) => (
              <div key={i} className="text-center" data-testid={`stat-${i}`}>
                <div className="font-display font-black text-4xl lg:text-6xl text-sky-400 tracking-tighter">
                  <Counter target={stat.value} />
                </div>
                <div className="mt-2 text-sm text-neutral-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {show("courses") && (
        <section className="py-24" data-testid="courses-section">
          <div className="container-eq">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
              <div>
                <div className="label-eq text-sky-500 mb-3">Programs</div>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tight">Our AI-Powered Programs</h2>
              </div>
              <Link to="/courses" className="text-sm font-semibold inline-flex items-center gap-1 hover:text-sky-500 transition-colors" data-testid="view-all-courses">
                View all courses <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {displayedCourses.map((c) => (
                <div key={c.id} className="card-eq overflow-hidden group" data-testid={`course-card-${c.slug}`}>
                  <div className="aspect-[16/10] overflow-hidden bg-neutral-100">
                    {c.thumbnail && <img src={c.thumbnail} alt={c.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                  </div>
                  <div className="p-6">
                    <div className="text-xs uppercase tracking-widest text-sky-500 font-semibold">{c.category}</div>
                    <h3 className="font-display font-bold text-xl mt-2">{c.title}</h3>
                    <p className="text-sm text-neutral-600 mt-1">{c.subtitle}</p>
                    <div className="flex items-center gap-4 mt-4 text-xs text-neutral-500">
                      <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {c.duration}</span>
                      <span className="font-bold text-neutral-900">{c.fees}</span>
                    </div>
                    <Link to={`/courses/${c.slug}`} className="mt-5 inline-flex items-center gap-1 text-sm font-semibold hover:text-sky-500 transition-colors" data-testid={`course-view-${c.slug}`}>
                      View details <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {show("why") && (
        <section className="py-20 lg:py-24 bg-white" data-testid="why-section">
          <div className="container-eq grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <div className="label-eq text-sky-500 mb-3">{s.home_intro?.kicker || "Why EQtech Academy"}</div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tight leading-tight" data-testid="home-intro-heading">
                {s.home_intro?.heading || "All-in-One Learning Platform for Competitive AI Careers"}
              </h2>
              <p className="mt-5 text-neutral-600 leading-relaxed text-base sm:text-lg" data-testid="home-intro-body">
                {s.home_intro?.body || "Sell your course with EQtech Academy. Partner with us, grow with us. EQtech Academy is a skill development marketplace where you market directly to students. Join and earn with practical world knowledge. 100% career-oriented academy with recorded sessions, live training, internships, and placement assistance."}
              </p>
              <div className="mt-8 grid sm:grid-cols-2 gap-4">
                {(s.why_choose || []).map((w, i) => (
                  <div key={i} className="p-5 rounded-2xl border border-neutral-200 bg-neutral-50" data-testid={`why-item-${i}`}>
                    <div className="w-10 h-10 rounded-xl bg-neutral-950 flex items-center justify-center mb-3">
                      {i === 0 && <Brain className="w-5 h-5 text-sky-400" />}
                      {i === 1 && <Users className="w-5 h-5 text-sky-400" />}
                      {i === 2 && <Zap className="w-5 h-5 text-sky-400" />}
                      {i === 3 && <Award className="w-5 h-5 text-sky-400" />}
                    </div>
                    <div className="font-display font-bold">{w.title}</div>
                    <div className="text-sm text-neutral-600 mt-1">{w.desc}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative order-first lg:order-last">
              <img src={s.home_intro?.image || "https://images.unsplash.com/photo-1677442135703-1787eea5ce01?w=1200"}
                   alt="AI-based skill academy — career-oriented learning at EQtech"
                   loading="lazy" decoding="async"
                   className="rounded-3xl w-full h-[300px] sm:h-[400px] lg:h-[500px] object-cover" data-testid="why-image" />
              {s.home_intro?.badge_visible !== false && (
                <div className="absolute -bottom-6 -left-2 sm:-left-6 bg-neutral-950 text-white p-5 sm:p-6 rounded-2xl max-w-[260px] sm:max-w-xs shadow-xl">
                  <Sparkles className="w-6 h-6 text-sky-400" />
                  <div className="mt-3 font-display font-bold text-base sm:text-lg leading-tight">
                    {s.home_intro?.badge_text || "AI is part of every module — not a separate topic."}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {show("partners") && (
        <section className="py-16 border-y border-neutral-200 bg-white" data-testid="partners-section">
          <div className="container-eq">
            <div className="text-center mb-8">
              <div className="label-eq text-neutral-500">Our students work at</div>
            </div>
            <div className="marquee">
              <div className="marquee-content">
                {[...s.hiring_partners, ...s.hiring_partners].map((p, i) => (
                  <div key={i} className="text-2xl lg:text-3xl font-display font-bold text-neutral-300 whitespace-nowrap">{p.name}</div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {show("testimonials") && (
        <section className="py-24 bg-neutral-50" data-testid="testimonials-section">
          <div className="container-eq">
            <div className="mb-12 text-center">
              <div className="label-eq text-sky-500 mb-3">Success Stories</div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tight">Hear from our students</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {testimonials.slice(0, 3).map((t) => (
                <div key={t.id} className="card-eq p-6" data-testid={`testimonial-${t.id}`}>
                  <Quote className="w-6 h-6 text-sky-400" />
                  <p className="mt-4 text-sm text-neutral-700 leading-relaxed">"{t.review}"</p>
                  <div className="flex gap-1 mt-4">
                    {Array.from({ length: t.rating || 5 }).map((_, i) => <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />)}
                  </div>
                  <div className="mt-4 flex items-center gap-3 pt-4 border-t border-neutral-100">
                    {t.image && <img src={t.image} alt={t.name} loading="lazy" decoding="async" className="w-10 h-10 rounded-full object-cover" />}
                    <div>
                      <div className="font-semibold text-sm">{t.name}</div>
                      <div className="text-xs text-neutral-500">{t.role} • {t.course}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {show("mobile_app") && <MobileAppSection />}

      {show("blogs_events") && (
        <section className="py-24" data-testid="blogs-events-section">
          <div className="container-eq grid lg:grid-cols-2 gap-12">
            <div>
              <div className="label-eq text-sky-500 mb-3">Latest Blogs</div>
              <h3 className="text-2xl font-display font-bold mb-6">Insights from experts</h3>
              <div className="space-y-4">
                {blogs.slice(0, 3).map((b) => (
                  <Link to={`/blog/${b.slug}`} key={b.id} className="flex gap-4 p-4 card-eq" data-testid={`blog-item-${b.slug}`}>
                    <div className="w-24 h-24 bg-neutral-100 rounded-xl overflow-hidden shrink-0">
                      {b.featured_image && <img src={b.featured_image} alt={b.title} loading="lazy" decoding="async" className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1">
                      <div className="text-xs uppercase tracking-widest text-sky-500 font-semibold">{b.category}</div>
                      <div className="font-display font-bold mt-1 leading-tight">{b.title}</div>
                      <div className="text-sm text-neutral-600 mt-1 line-clamp-2">{b.excerpt}</div>
                    </div>
                  </Link>
                ))}
                {blogs.length === 0 && <div className="text-sm text-neutral-500">New articles coming soon.</div>}
              </div>
            </div>
            <div>
              <div className="label-eq text-sky-500 mb-3">Upcoming Events</div>
              <h3 className="text-2xl font-display font-bold mb-6">Join our workshops</h3>
              <div className="space-y-4">
                {events.slice(0, 3).map((ev) => (
                  <div key={ev.id} className="p-5 card-eq" data-testid={`event-item-${ev.id}`}>
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 bg-neutral-950 text-white rounded-xl flex flex-col items-center justify-center shrink-0">
                        <Calendar className="w-5 h-5 text-sky-400" />
                      </div>
                      <div>
                        <div className="text-xs text-sky-500 font-semibold">{ev.date}</div>
                        <div className="font-display font-bold">{ev.title}</div>
                        <div className="text-sm text-neutral-600">{ev.description}</div>
                      </div>
                    </div>
                  </div>
                ))}
                {events.length === 0 && <div className="text-sm text-neutral-500">New events coming soon.</div>}
              </div>
            </div>
          </div>
        </section>
      )}

      {show("faq") && (
        <section className="py-24 bg-white" data-testid="faq-section">
          <div className="container-eq max-w-4xl">
            <div className="text-center mb-12">
              <div className="label-eq text-sky-500 mb-3">FAQ</div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tight">Frequently Asked Questions</h2>
            </div>
            <div className="space-y-3">
              {faqs.map((f) => (
                <div key={f.id} className="border border-neutral-200 rounded-2xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === f.id ? null : f.id)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-neutral-50"
                    data-testid={`faq-toggle-${f.id}`}
                  >
                    <span className="font-semibold">{f.question}</span>
                    <ChevronRight className={`w-5 h-5 shrink-0 transition-transform ${openFaq === f.id ? "rotate-90" : ""}`} />
                  </button>
                  {openFaq === f.id && <div className="px-5 pb-4 text-sm text-neutral-600 leading-relaxed">{f.answer}</div>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {show("cta_contact") && (
        <section className="py-24 bg-neutral-950 text-white" data-testid="contact-cta-section">
          <div className="container-eq grid lg:grid-cols-2 gap-12">
            <div>
              <div className="label-eq text-sky-400 mb-3">Get in Touch</div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tight leading-tight">Ready to launch your AI marketing career?</h2>
              <p className="mt-5 text-neutral-400 leading-relaxed">Schedule a free counseling session and discover the right program for you.</p>
              <div className="mt-8 space-y-3 text-sm">
                <div>📞 {s.contact.phone}</div>
                <div>✉ {s.contact.email}</div>
                <div>📍 {s.contact.address}</div>
                <div>🕒 {s.contact.hours}</div>
              </div>
            </div>
            <div className="bg-white text-neutral-900 rounded-3xl p-8">
              <h3 className="font-display font-bold text-2xl mb-6">Request a Callback</h3>
              <LeadForm source="homepage_cta" compact />
            </div>
          </div>
        </section>
      )}
    </>
  );
}
